import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusPill } from "@/components/StatusPill";
import { Pagination } from "@/components/Pagination";
import { Plus, Search, Users, Download, Cake } from "lucide-react";
import { format } from "date-fns";

const PAGE_SIZE = 25;

export default function ContactsListPage() {
  const nav = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ search: "", contact_type: "all", upcoming: "off" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (filters.search) params.search = filters.search;
      if (filters.contact_type && filters.contact_type !== "all") params.contact_type = filters.contact_type;
      if (filters.upcoming === "on") params.upcoming_occasion_days = 30;
      const { data } = await api.get("/contacts", { params });
      setContacts(data.items || []);
      setTotal(data.total || 0);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filters, page]);
  useEffect(() => { setPage(1); }, [filters.search, filters.contact_type, filters.upcoming]);

  const exportCsv = async () => {
    const res = await api.get("/contacts/export/csv", { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a"); a.href = url; a.download = "contacts.csv"; document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div className="space-y-6" data-testid="contacts-list-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">CRM</div>
          <h1 className="font-serif text-3xl lg:text-4xl font-medium">Contacts</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} className="rounded-full" data-testid="export-contacts-btn"><Download size={14} className="mr-1"/> Export CSV</Button>
          <Button onClick={() => nav("/contacts/new")} className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="new-contact-btn"><Plus size={14} className="mr-1"/> New Contact</Button>
        </div>
      </div>

      <Card className="border-[#E4DFCF] rounded-xl">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5A50]"/>
            <Input placeholder="Search name, mobile, city…" className="pl-9 rounded-lg" value={filters.search} onChange={(e)=>setFilters({...filters, search:e.target.value})} data-testid="contacts-search"/>
          </div>
          <Select value={filters.contact_type} onValueChange={(v)=>setFilters({...filters, contact_type:v})}>
            <SelectTrigger className="rounded-lg" data-testid="filter-contact-type"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="lead">Leads</SelectItem>
              <SelectItem value="customer">Customers</SelectItem>
              <SelectItem value="vendor">Vendors</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.upcoming} onValueChange={(v)=>setFilters({...filters, upcoming:v})}>
            <SelectTrigger className="rounded-lg" data-testid="filter-upcoming"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="off">All</SelectItem>
              <SelectItem value="on">Occasion within 30 days</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {loading ? <div className="text-center py-12 text-[#4A5A50]">Loading…</div> : contacts.length === 0 ? (
        <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="py-16 text-center"><Users size={32} className="text-[#C9A24B] mx-auto mb-3" strokeWidth={1.5}/><div className="font-serif text-xl">No contacts</div><div className="text-[#4A5A50] text-sm">Add your first contact to start tracking relationships.</div></CardContent></Card>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {contacts.map(c => (
            <button key={c.id} onClick={() => nav(`/contacts/${c.id}`)} className="text-left bg-white rounded-xl border border-[#E4DFCF] p-5 card-hover" data-testid={`contact-card-${c.mobile || c.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-serif text-xl">{c.name}</div>
                <StatusPill status={c.contact_type}/>
              </div>
              <div className="text-sm text-[#4A5A50] mt-1">{c.mobile || "—"} · {c.city || "—"}</div>
              <div className="mt-3 flex flex-wrap gap-1">
                {(c.tags || []).slice(0, 4).map((t,i) => <span key={i} className="text-[10px] uppercase tracking-widest bg-[#F6EED8] text-[#6E5416] px-2 py-1 rounded-full">{t}</span>)}
              </div>
              {(c.important_dates || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#E4DFCF] flex items-center gap-1 text-xs text-[#4A5A50]">
                  <Cake size={12}/>
                  {c.important_dates[0].label} · {format(new Date(c.important_dates[0].date), "dd MMM")}
                </div>
              )}
            </button>
          ))}
        </div>
        <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage}/>
        </>
      )}
    </div>
  );
}
